/**
 * PeriodComparisonBar — shows "this period vs. previous period" as two bars.
 * Best for very short date ranges or sparse data where even weekly bars
 * would have only 1–2 columns.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import type { PeriodComparisonData } from '@/utils/chart-adaptation';

interface PeriodComparisonBarProps {
  data: PeriodComparisonData;
  color: string;
  height?: number;
}

const PeriodComparisonBar: React.FC<PeriodComparisonBarProps> = ({
  data,
  color,
  height = 300,
}) => {
  const chartData = [
    { name: data.previousPeriodLabel, value: data.previousPeriodTotal, period: 'previous' },
    { name: data.currentPeriodLabel, value: data.currentPeriodTotal, period: 'current' },
  ];

  const isPositive = data.changePercent > 0;
  const changeLabel = `${isPositive ? '+' : ''}${data.changePercent.toFixed(1)}%`;

  return (
    <div className="space-y-3">
      {/* Change indicator */}
      <div className="flex items-center justify-center space-x-2">
        {data.changePercent !== 0 && (
          <>
            {isPositive ? (
              <ArrowTrendingUpIcon className="w-4 h-4 text-danger-500" />
            ) : (
              <ArrowTrendingDownIcon className="w-4 h-4 text-success-500" />
            )}
            <span
              className={`text-sm font-semibold ${
                isPositive
                  ? 'text-danger-600 dark:text-danger-400'
                  : 'text-success-600 dark:text-success-400'
              }`}
            >
              {changeLabel} vs previous period
            </span>
          </>
        )}
        {data.changePercent === 0 && (
          <span className="text-sm text-secondary-500 dark:text-secondary-400">
            No change vs previous period
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={height - 40}>
        <BarChart data={chartData} barGap={16}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={{ stroke: '#cbd5e1' }}
            axisLine={{ stroke: '#cbd5e1' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#64748b' }}
            tickLine={{ stroke: '#cbd5e1' }}
            axisLine={{ stroke: '#cbd5e1' }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
            }}
            labelStyle={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}
          />
          <Bar dataKey="value" name="Total" radius={[8, 8, 0, 0]} animationDuration={300} animationEasing="ease-out">
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={color}
                opacity={entry.period === 'previous' ? 0.5 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PeriodComparisonBar;
